import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { stepIcon } from "./stepIcons";

/**
 * El menú lateral MIENTRAS dura la puesta en marcha: los seis pasos en su orden,
 * y nada más.
 *
 * La puerta (`OnboardingGate`) ya obligaba a hacerlos en orden, pero en silencio:
 * quien pulsaba Inventario antes de tiempo volvía rebotado sin que nadie le
 * dijera por qué. Aquí esa regla se ve — candado en lo que aún no toca — así que
 * el rebote deja de existir porque el enlace ni siquiera se ofrece.
 *
 * Un paso bloqueado se pinta como `<div>`, no como enlace ni botón: no se puede
 * pulsar, no entra en el tabulador y los lectores de pantalla lo anuncian como
 * deshabilitado.
 */
interface OnboardingStepsNavProps {
  collapsed?: boolean;
  onNavigate?: () => void;
}

export function OnboardingStepsNav({ collapsed = false, onNavigate }: OnboardingStepsNavProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { steps, completedSteps, isLoading } = useOnboarding();

  if (isLoading) {
    return (
      <div className={cn("space-y-1.5", collapsed ? "px-0 items-center flex flex-col" : "px-2")}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className={cn("rounded-xl bg-white/15 animate-pulse", collapsed ? "h-[44px] w-[44px]" : "h-[38px] w-full")}
          />
        ))}
      </div>
    );
  }

  // El primero sin completar es el que toca; todo lo que venga después, cerrado.
  const firstPendingIndex = steps.findIndex((s) => !completedSteps.includes(s.step_key));

  return (
    <nav aria-label={t("nav.gettingStarted")} className={cn(collapsed ? "px-0" : "px-2")}>
      <ol className={cn("space-y-1", collapsed && "flex flex-col items-center space-y-2")}>
        {steps.map((step, index) => {
          const done = completedSteps.includes(step.step_key);
          const isCurrent = index === firstPendingIndex;
          // Sin ningún paso pendiente (firstPendingIndex === -1) no queda nada
          // cerrado: la guía está terminada y se puede volver a cualquier paso.
          const locked = firstPendingIndex !== -1 && index > firstPendingIndex;
          const Icon = stepIcon(step.icon);
          const label = t(step.title_key, step.step_key);
          const active = !locked && location.pathname.startsWith(step.route.split("?")[0]);

          const inner = (
            <>
              <span
                className={cn(
                  "flex items-center justify-center shrink-0 rounded-lg transition-colors",
                  collapsed ? "w-[26px] h-[26px]" : "w-[24px] h-[24px]",
                  done ? "bg-emerald-500 text-white" : isCurrent ? "bg-white text-[#2E84DE]" : "bg-white/10 text-white/40",
                )}
              >
                {done ? <Check className="w-3.5 h-3.5" /> : <Icon className={cn("w-[15px] h-[15px]", step.icon === "map" && "text-black")} />}
              </span>
              {!collapsed && (
                <>
                  <span className={cn("flex-1 min-w-0 truncate text-left", locked ? "text-white/40" : "text-white")}>
                    {label}
                  </span>
                  {locked && <Lock className="w-3.5 h-3.5 shrink-0 text-white/35" />}
                </>
              )}
            </>
          );

          const base = cn(
            "w-full flex items-center rounded-xl text-[13px] font-semibold transition-colors",
            collapsed ? "justify-center h-[44px] w-[44px] p-0" : "gap-2.5 h-[38px] px-2.5",
            active && !locked && "bg-white/20",
          );

          if (locked) {
            return (
              <li key={step.id}>
                <div
                  aria-disabled="true"
                  title={collapsed ? label : undefined}
                  className={cn(base, "cursor-default select-none")}
                >
                  {inner}
                </div>
              </li>
            );
          }

          return (
            <li key={step.id}>
              <button
                type="button"
                title={collapsed ? label : undefined}
                aria-current={active ? "page" : undefined}
                onClick={() => {
                  navigate(step.route);
                  onNavigate?.();
                }}
                className={cn(base, "hover:bg-white/15")}
              >
                {inner}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
